import type { TableColumnStaticProps } from '@/types/tableTypes';

/**
 * Static column schema for the Playlists report page filter / columns UI —
 * the frontend analogue of {@link
 * file:tabasco/app/Services/PlaylistSwitchingFilterSchema.php}.
 *
 * The list query is a GROUP BY aggregate, so only the list fields (name, code)
 * are filterable (WHERE); the aggregate columns (switches, switchers) are
 * sortable but not filterable. Mirrors the audio-file-issues page-level schema
 * approach (see {@link AUDIO_FILE_ISSUES_ENTITY_ID}).
 *
 * Keys are persisted in user filter / column preferences (Pinia + localStorage);
 * renaming one invalidates saved state — bump the entity id instead.
 */
export const PLAYLIST_SWITCHING_ENTITY_ID = 'PlaylistSwitching';

const baseStaticProps = {
  isFilled: false,
  isProhibited: false,
  isUnique: false,
} as const;

function staticColumn(
  id: string,
  header: string,
  dataType: 'string' | 'number' | 'Date' | 'display',
  options: { isNullable?: boolean; isSortable?: boolean; isFilterable?: boolean } = {},
): TableColumnStaticProps {
  return {
    id,
    key: id,
    field: id,
    header,
    dataType,
    isSortable: options.isSortable ?? true,
    isFilterable: options.isFilterable ?? true,
    isNullable: options.isNullable ?? false,
    ...baseStaticProps,
  };
}

export const PLAYLIST_SWITCHING_COLUMNS: TableColumnStaticProps[] = [
  staticColumn('name', 'List', 'string', { isNullable: true }),
  // The list's own id — identity column, not in the sort/filter schema.
  staticColumn('location_zone_custom_playlist_id', 'List id', 'number', { isSortable: false, isFilterable: false }),
  staticColumn('code', 'Code', 'string', { isNullable: true }),
  // Filterable list fields (the backend IScope schema accepts name/code/status/bucket).
  staticColumn('status_name', 'Status', 'string', { isNullable: true }),
  staticColumn('source_bucket_name', 'Source bucket', 'string', { isNullable: true }),
  // Sortable-only list fields (not in the IScope filter schema).
  staticColumn('source_bucket_id', 'Source bucket id', 'number', { isNullable: true, isFilterable: false }),
  // Where the list "comes from": the zone it belongs to, by id and by name.
  //
  // `location_zone_name` is the zone's DISPLAY STRING — "{location}-{zone}", composed in
  // SQL by PlaylistSwitchingController::ZONE_DISPLAY_STRING_SQL, because the location name
  // is what tells one "Main Floor" from another. This comment used to claim as much while
  // the controller selected `lz.name` alone, and the claim is why nobody noticed: the
  // column rendered a plausible-looking zone name, so only the comment said it was wrong.
  // If you change the column, change the SQL — do not re-describe it here and hope.
  staticColumn('location_zone_name', 'Zone', 'string', { isNullable: true, isFilterable: false }),
  staticColumn('location_zone_id', 'Zone id', 'number', { isNullable: true, isFilterable: false }),
  staticColumn('shuffle', 'Shuffle', 'string', { isFilterable: false }),
  staticColumn('user_can_shuffle', 'User can shuffle', 'string', { isFilterable: false }),
  staticColumn('artist_repeat_limit_hours', 'Artist repeat (hrs)', 'number', { isNullable: true, isFilterable: false }),
  staticColumn('download_priority', 'Download priority', 'number', { isNullable: true, isFilterable: false }),
  staticColumn('status_expiration', 'Status expires', 'Date', { isNullable: true, isFilterable: false }),
  staticColumn('created_at', 'Created', 'Date', { isFilterable: false }),
  staticColumn('updated_at', 'Updated', 'Date', { isFilterable: false }),
  // Aggregates: sortable, but not filter-builder fields (would need HAVING).
  staticColumn('total_switch_count', 'Switches', 'number', { isFilterable: false }),
  staticColumn('distinct_switcher_count', 'Switchers', 'number', { isFilterable: false }),
];

/**
 * Column ids whose table cell renders right-aligned with `tabular-nums` and runs
 * through `formatNumber`. Derived from the schema's `dataType` so every numeric
 * column is formatted consistently — adding a numeric column to
 * {@link PLAYLIST_SWITCHING_COLUMNS} opts it in automatically, rather than each new
 * id having to be remembered in the page's cell renderer (which is how List id /
 * Zone id originally shipped left-aligned next to Source bucket id).
 */
export const PLAYLIST_SWITCHING_NUMERIC_COLUMN_IDS: ReadonlySet<string> = new Set(
  PLAYLIST_SWITCHING_COLUMNS.filter((column) => column.dataType === 'number').map((column) => column.id),
);

/**
 * Columns visible on first load. The rest are revealed through the Columns
 * inspector (so the on/off toggle has real columns to work with).
 */
export const PLAYLIST_SWITCHING_DEFAULT_VISIBLE: ReadonlySet<string> = new Set([
  'name',
  'location_zone_custom_playlist_id',
  'code',
  'status_name',
  'location_zone_name',
  'location_zone_id',
  'source_bucket_name',
  'source_bucket_id',
  'total_switch_count',
  'distinct_switcher_count',
]);

/** Default column widths in px. */
export const PLAYLIST_SWITCHING_DEFAULT_WIDTHS: Record<string, number> = {
  name: 260,
  location_zone_custom_playlist_id: 110,
  code: 140,
  status_name: 160,
  location_zone_name: 200,
  location_zone_id: 110,
  source_bucket_name: 200,
  source_bucket_id: 130,
  shuffle: 100,
  user_can_shuffle: 140,
  artist_repeat_limit_hours: 150,
  download_priority: 150,
  status_expiration: 150,
  created_at: 150,
  updated_at: 150,
  total_switch_count: 120,
  distinct_switcher_count: 120,
};

/** Default sort: most-switched first. */
export const PLAYLIST_SWITCHING_DEFAULT_SORT: Record<string, 'asc' | 'desc'> = {
  total_switch_count: 'desc',
};
